import { useState, useEffect, type FormEvent } from 'react'
import { toast } from 'sonner'
import { RefreshCw, RotateCcw, Loader2, CheckCircle, XCircle, AlertCircle } from 'lucide-react'
import { ApiError } from '@/lib/api-error'
import { useProxyConfig } from '@/hooks/useProxyConfig'
import { useConnectionStatus } from '@/hooks/useConnectionStatus'
import { useProviderTapTrigger } from '@/hooks/useProviderTrigger'
import { ProviderAuthGate } from '@/components/shared/ProviderAuthGate'
import { PasswordInput } from '@/components/ui/PasswordInput'

interface LoginProps {
  onLogin: (email: string, password: string) => Promise<void>
}

type View = 'login' | 'provider'

export function Login({ onLogin }: LoginProps) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [fieldError, setFieldError] = useState<string | null>(null)
  const [checkingUpdate, setCheckingUpdate] = useState(false)
  const [resetting, setResetting] = useState(false)
  const [view, setView] = useState<View>('login')
  const [businessName, setBusinessName] = useState<string | null>(null)
  const [businessLogoUrl, setBusinessLogoUrl] = useState<string | null>(null)
  const handleLogoTap = useProviderTapTrigger()
  const { isElectron, appVersion, proxyUrl, proxyToken } = useProxyConfig()
  const connStatus = useConnectionStatus()

  // Load public business config for hero branding
  useEffect(() => {
    if (!proxyUrl) return
    fetch(`${proxyUrl}/api/config/public`, {
      headers: proxyToken ? { Authorization: `Bearer ${proxyToken}` } : {},
    })
      .then(r => r.ok ? r.json() : null)
      .then((d: any) => {
        if (d) {
          setBusinessName(d.business_name || null)
          setBusinessLogoUrl(d.business_logo_url || null)
        }
      })
      .catch(() => {})
  }, [proxyUrl])

  if (view === 'provider') {
    return (
      <div className="animate-[fade-in_0.2s_ease]">
        <ProviderAuthGate onBack={() => setView('login')} />
      </div>
    )
  }

  async function handleCheckUpdate() {
    if (!proxyUrl || !proxyToken) return
    setCheckingUpdate(true)
    try {
      const res = await fetch(`${proxyUrl}/system/check-updates`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${proxyToken}` },
      })
      const result = await res.json()
      if (result.status === 'up-to-date') {
        toast.success('Estás en la última versión ✓')
      } else if (result.status === 'update-available') {
        toast.success(`Nueva versión ${result.version} disponible — descargando...`)
      } else if (result.status === 'not-packaged') {
        toast('Actualizaciones no disponibles en modo desarrollo')
      } else {
        toast.error(result.message || 'Error al verificar actualizaciones')
      }
    } catch {
      toast.error('No se pudo conectar para verificar actualizaciones')
    } finally {
      setCheckingUpdate(false)
    }
  }

  async function handleResetConfig() {
    if (!proxyUrl) return
    setResetting(true)
    try {
      const h: Record<string, string> = proxyToken ? { Authorization: `Bearer ${proxyToken}` } : {}
      await fetch(`${proxyUrl}/local/config`, { method: 'DELETE', headers: h })
      toast.success('Configuración local eliminada. Reinicia la aplicación.')
    } catch {
      toast.error('Error al resetear la configuración')
    } finally {
      setResetting(false)
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setFieldError(null)
    setLoading(true)
    try {
      await onLogin(email, password)
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Error al iniciar sesión'
      setFieldError(message)
    } finally {
      setLoading(false)
    }
  }

  const displayName = businessName || 'Veridia'

  return (
    <div className="min-h-screen flex animate-[fade-in_0.2s_ease]">

      {/* ── Left hero panel (desktop only) ──────────────────────────────────── */}
      <div
        className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12 text-white relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #0c1c2e 0%, #0b2d1f 45%, #0e1a32 100%)' }}
      >
        {/* Decorative orbs — mesh gradient effect */}
        <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full blur-3xl pointer-events-none"
             style={{ background: 'radial-gradient(circle, hsl(160 84% 39% / 0.45) 0%, transparent 70%)' }} />
        <div className="absolute bottom-0 -left-24 w-[400px] h-[400px] rounded-full blur-3xl pointer-events-none"
             style={{ background: 'radial-gradient(circle, #4f46e5 0%, transparent 70%)', opacity: 0.25 }} />
        <div className="absolute top-1/2 left-1/3 w-64 h-64 rounded-full blur-2xl pointer-events-none"
             style={{ background: 'radial-gradient(circle, hsl(160 84% 39% / 0.15) 0%, transparent 70%)' }} />

        {/* Top: logo + name */}
        <div className="relative flex items-center gap-3">
          {businessLogoUrl ? (
            <img src={businessLogoUrl} alt={displayName} className="h-8 w-auto object-contain cursor-pointer select-none" onClick={handleLogoTap} />
          ) : (
            <div className="w-9 h-9 bg-white/10 border border-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm cursor-pointer select-none" onClick={handleLogoTap}>
              <span className="text-white font-bold text-sm">IS</span>
            </div>
          )}
          <span className="font-bold text-lg tracking-tight text-white/90">{displayName}</span>
        </div>

        {/* Center: headline */}
        <div className="relative">
          <div className="inline-flex items-center gap-2 bg-white/8 border border-white/12 rounded-full px-3 py-1 text-xs text-white/60 font-medium mb-6 backdrop-blur-sm">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
            Sistema de gestión
          </div>
          <h1 className="text-[2.6rem] font-bold leading-[1.15] tracking-tight mb-4 text-white">
            Gestión de acceso<br />
            <span style={{ background: 'linear-gradient(90deg, hsl(160 84% 55%), hsl(160 84% 75%))', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              sin complicaciones.
            </span>
          </h1>
          <p className="text-white/50 text-base leading-relaxed">
            Control de membresías, acceso biométrico<br />y reportes en un solo lugar.
          </p>

          {/* Connection status */}
          {isElectron && connStatus.state !== 'noProxy' && (
            <div className="mt-7">
              {connStatus.state === 'checking' && (
                <div className="flex items-center gap-2 text-white/40 text-sm">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Verificando conexión...
                </div>
              )}
              {connStatus.state === 'connected' && (
                <div className="inline-flex items-center gap-2 bg-emerald-500/15 border border-emerald-500/25 rounded-full px-3 py-1.5 text-emerald-400 text-xs font-medium">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                  Conectado{connStatus.profileName ? ` · ${connStatus.profileName}` : ''}
                </div>
              )}
              {(connStatus.state === 'error' || connStatus.state === 'noDB') && (
                <div className="flex items-center gap-2 text-white/40 text-sm">
                  <div className="w-1.5 h-1.5 rounded-full bg-red-400/60" />
                  {connStatus.state === 'noDB' ? 'Sin base de datos' : 'Sin conexión'}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Bottom: version */}
        <div className="relative text-white/25 text-xs font-mono">
          {appVersion ? `v${appVersion}` : ''}
        </div>
      </div>

      {/* ── Right form panel ─────────────────────────────────────────────────── */}
      <div
        className="flex-1 flex flex-col items-center justify-center p-8 relative overflow-hidden"
        style={{ background: 'linear-gradient(160deg, #0e1a32 0%, #0c1c2e 60%, #0b2d1f 100%)' }}
      >
        {/* Subtle orb behind form */}
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full blur-3xl pointer-events-none"
             style={{ background: 'radial-gradient(circle, hsl(160 84% 39% / 0.07) 0%, transparent 65%)' }} />

        <div className="relative w-full max-w-md">

          {/* Mobile: compact logo */}
          <div className="flex items-center gap-3 mb-8 lg:hidden">
            {businessLogoUrl ? (
              <img src={businessLogoUrl} alt={displayName} className="h-8 w-auto object-contain cursor-pointer select-none" onClick={handleLogoTap} />
            ) : (
              <div className="w-9 h-9 bg-white/10 border border-white/15 rounded-xl flex items-center justify-center cursor-pointer select-none" onClick={handleLogoTap}>
                <span className="text-white font-bold text-sm">IS</span>
              </div>
            )}
            <span className="text-white font-bold text-xl">{displayName}</span>
          </div>

          {/* Connection status badge — mobile only */}
          {isElectron && connStatus.state !== 'checking' && connStatus.state !== 'noProxy' && (
            <div className={`lg:hidden flex items-center gap-2 text-xs px-3 py-2 rounded-xl mb-4 transition-all border ${
              connStatus.state === 'connected'
                ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                : 'bg-red-500/10 text-red-400 border-red-500/20'
            }`}>
              {connStatus.state === 'connected'
                ? <CheckCircle className="w-3.5 h-3.5 shrink-0" />
                : <XCircle className="w-3.5 h-3.5 shrink-0" />
              }
              <span className="flex-1 truncate">
                {connStatus.state === 'connected'
                  ? `Conectado${connStatus.profileName ? ` · ${connStatus.profileName}` : ''}`
                  : connStatus.state === 'noDB'
                    ? 'Sin base de datos configurada'
                    : `Sin conexión${connStatus.error ? ` · ${connStatus.error}` : ''}`
                }
              </span>
            </div>
          )}
          {isElectron && connStatus.state === 'checking' && (
            <div className="lg:hidden flex items-center gap-2 text-xs px-3 py-2 rounded-xl mb-4 bg-white/5 text-white/40 border border-white/8">
              <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
              Verificando conexión...
            </div>
          )}

          <h2 className="text-white font-bold text-2xl mb-1 tracking-tight">Iniciar sesión</h2>
          <p className="text-white/45 text-sm mb-8">Ingresa tus credenciales para continuar.</p>

          <form onSubmit={handleSubmit} className="flex flex-col gap-5">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="email" className="text-white/40 text-xs font-medium uppercase tracking-wide">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={e => { setEmail(e.target.value); setFieldError(null) }}
                required
                autoComplete="email"
                disabled={loading}
                className={`bg-white/5 border rounded-xl px-4 py-3 text-sm text-white placeholder:text-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40 focus-visible:ring-offset-1 focus-visible:ring-offset-transparent transition-all disabled:opacity-50 ${
                  fieldError ? 'border-red-500/50' : 'border-white/10 hover:border-white/20'
                }`}
                placeholder="admin@ejemplo.com"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="password" className="text-white/40 text-xs font-medium uppercase tracking-wide">
                Contraseña
              </label>
              <PasswordInput
                id="password"
                value={password}
                onChange={e => { setPassword(e.target.value); setFieldError(null) }}
                required
                autoComplete="current-password"
                disabled={loading}
                error={!!fieldError}
                placeholder="••••••••"
                className={`bg-white/5 border rounded-xl px-4 py-3 text-white placeholder:text-white/20 focus-visible:ring-2 focus-visible:ring-emerald-500/40 focus-visible:ring-offset-1 focus-visible:ring-offset-transparent transition-all ${
                  fieldError ? 'border-red-500/50' : 'border-white/10 hover:border-white/20'
                }`}
                iconClassName="text-white/30 hover:text-white/70"
              />
            </div>

            {fieldError && (
              <div className="flex items-center gap-2 text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2.5">
                <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                {fieldError}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="mt-1 w-full flex items-center justify-center gap-2 text-white font-semibold text-sm py-3.5 rounded-xl transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: loading ? 'linear-gradient(135deg, #1d4ed8, #3730a3)' : 'linear-gradient(135deg, #2563eb, #4f46e5)' }}
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {loading ? 'Ingresando...' : 'Ingresar'}
            </button>
          </form>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-white/8 flex flex-col gap-4">

            {/* Version + utilidades */}
            {isElectron && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  {appVersion && (
                    <span className="inline-flex items-center gap-1.5 bg-white/8 text-white/60 text-xs font-mono font-semibold px-2.5 py-1 rounded-full border border-white/10">
                      v{appVersion}
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={handleResetConfig}
                    disabled={resetting}
                    title="Eliminar configuración local"
                    className="flex items-center gap-1.5 text-xs font-medium text-red-400 bg-red-500/10 border border-red-500/20 hover:bg-red-500/20 hover:border-red-500/40 px-2.5 py-1.5 rounded-lg transition-all disabled:cursor-not-allowed"
                  >
                    {resetting ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                    {resetting ? 'Reseteando...' : 'Resetear'}
                  </button>
                  {appVersion && (
                    <button
                      type="button"
                      onClick={handleCheckUpdate}
                      disabled={checkingUpdate}
                      className="flex items-center gap-1.5 text-xs font-medium text-white/50 bg-white/5 border border-white/10 hover:bg-white/10 hover:text-white px-2.5 py-1.5 rounded-lg transition-all disabled:cursor-not-allowed"
                    >
                      {checkingUpdate ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                      {checkingUpdate ? 'Buscando...' : 'Actualizar'}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
